import React, { useContext, useEffect, useRef, useState } from 'react';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

const errorMessage: string = 'Something went wrong!';

interface Props {
    url: string;
    parserFunction: (...args: any[]) => {};
    method: 'post' | 'get' | 'put' | 'delete';
    queryKey: any[];
    onComplete?: Function;
    onError?: Function;
    variables?: any;
    paramsVariables?: any;
    appConfig?: any;
    queryConfig?: {
        enabled?: boolean;
        cacheTime?: number;
        staleTime?: number;
    };
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
    count: number;
    isError?: boolean;
}

function useCommonApiHandler({
    url,
    parserFunction,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    paramsVariables = {},
    appConfig = {},
    queryConfig = {},
}: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
        isError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            if (!variables?.offset) {
                //initializing states
                setData(null);
                setError(null);
            }
            setLoading(true);
            try {
                const data = await axios({
                    method,
                    url,
                    data: { ...variables },
                    params: { ...paramsVariables },
                });
                return data;
            } catch (error: any) {
                onFetchError(error?.message);
                return { data: { error: error?.message } };
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }

    //error handling when fetching from cache
    if (apiData?.data && 'error' in apiData?.data && !error) {
        onFetchError(apiData?.data?.error);
    }

    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = parserFunction(payload, appConfig);
            // send { error: true } to fail it
            if (!parsedData?.error) {
                setData(parsedData);
                setCount(payload?.total_count ?? 0);
            }
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
        setCount(0);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
        isError,
    };
    return outputData;
}

export { useCommonApiHandler };
